import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';

import { CdkScrollable } from '@angular/cdk/scrolling';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  ReactiveFormsModule,
  UntypedFormControl,
} from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import {
  MatButton,
  MatIconButton,
  MatMiniFabButton,
} from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';

import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
  MatMenu,
  MatMenuContent,
  MatMenuItem,
  MatMenuTrigger,
} from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { BaseComponent } from '@shared/components/base/base-component/base.component';
import { ConfirmationDialogService } from '@shared/components/confirmation-dialog/confirmation-dialog.service';
import { NormalizeString } from '@shared/util/normalize-string';
import { VexSidebarComponent } from '@vex/components/vex-sidebar/vex-sidebar.component';
import { filter, startWith, switchMap } from 'rxjs';
import { DashboardPreferenciaCardModel } from '../../models/dashboard-config.model';
import {
  DashboardCardModel,
  DashboardCardTipo,
  DashboardCardTipoType,
} from '../../models/dashboard.model';
import { DashboardService } from '../../services/dashboard.service';
import { DashboardCriarCardComponent } from '../criar-card/dashboard-criar-card.component';

type CardSelModel = DashboardCardModel &
  Partial<DashboardPreferenciaCardModel> & { sel: FormControl<boolean> };

@Component({
  selector: 'app-dashboard-selecionar-card',
  imports: [
    CdkDrag,
    CdkDragHandle,
    CdkDropList,
    CdkScrollable,
    DashboardCriarCardComponent,
    MatBadgeModule,
    MatButton,
    MatCheckbox,
    MatFormField,
    MatIcon,
    MatIconButton,
    MatInput,
    MatMenu,
    MatMenuContent,
    MatMenuItem,
    MatMenuTrigger,
    MatMiniFabButton,
    MatTooltip,
    ReactiveFormsModule,
    VexSidebarComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Default,
  templateUrl: './dashboard-selecionar-card.component.html',
  styleUrls: ['./dashboard-selecionar-card.component.scss'],
  host: {
    tabindex: '0',
  },
})
export class DashboardSelecionarCardComponent extends BaseComponent {
  // Input
  sidebarComponent = input<VexSidebarComponent>();

  // Injectable
  private cdr = inject(ChangeDetectorRef);
  private confirmSvc = inject(ConfirmationDialogService);
  private dashboardSvc = inject(DashboardService);

  // Atributos protected
  protected searchControl = new UntypedFormControl();

  // Signals private
  private prefUsuario = computed(() =>
    this.dashboardSvc.getPreferenciaUsuario()()
  );
  private searchSignal = toSignal(
    this.searchControl.valueChanges.pipe(startWith(null))
  );

  // Signals protected
  protected cardsSel = signal<CardSelModel[]>([]);
  protected filteredCards = computed(() => this.filterCards());

  // Effects
  private cardsChanged = effect(() => {
    this.cardsSel.set(this.configCards());
  });

  // Métodos protected
  protected getIconTipoCard(tipoCard: DashboardCardTipoType) {
    switch (tipoCard) {
      case DashboardCardTipo.LISTA:
        return 'mat:format_list_bulleted';
      case DashboardCardTipo.GRAFICO_COLUNA:
        return 'mat:bar_chart';
      case DashboardCardTipo.GRAFICO_LINHA:
        return 'mat:show_chart';
      case DashboardCardTipo.GRAFICO_PIZZA:
        return 'mat:pie_chart';
      case DashboardCardTipo.TOTALIZADOR:
        return 'mat:functions';
    }
  }

  protected onChangeSel(card: CardSelModel) {
    const cardsSel = [...this.cardsSel()];
    const indexOrigem = cardsSel.findIndex((c) => c === card);
    if (indexOrigem === -1) {
      return;
    }

    let indexDestino = 0;
    if (card.sel.value) {
      indexDestino = cardsSel.findIndex((item) => {
        return !item.sel.value;
      });

      // Se o último item marcado for o próprio item não é necessário mover
      if (cardsSel[indexDestino - 1] === card) {
        return;
      }
    } else {
      indexDestino =
        cardsSel.findIndex(
          (item) =>
            !item.sel.value && item.titulo.localeCompare(card.titulo) === 1
        ) - 1;
    }
    moveItemInArray(
      cardsSel,
      indexOrigem,
      indexDestino < 0 ? cardsSel.length : indexDestino
    );

    this.cardsSel.set(cardsSel);
    this.cdr.detectChanges();
  }

  protected onDrop(event: CdkDragDrop<CardSelModel[]>) {
    const cardsSel = this.cardsSel();
    const filteredCards = this.filteredCards();

    // Procura os cards na lista não filtrada
    const indexOrigem = cardsSel.findIndex((cs) => cs === event.item.data);
    const indexDestino = cardsSel.findIndex(
      (cs) => cs === filteredCards[event.currentIndex]
    );
    if (indexOrigem === -1 || indexDestino === -1) {
      return;
    }

    // Move card na lista não filtrada
    moveItemInArray(cardsSel, indexOrigem, indexDestino);
    this.cardsSel.set(cardsSel);
  }

  protected onExcluirCard(card: CardSelModel) {
    this.confirmSvc
      .openDialog({
        mensagem: 'Confirma a exclusão do Card?',
        titulo: 'Exclusão de card',
        tipo: 'aviso',
      })
      .pipe(
        filter((resp) => resp.resposta === 'S'),
        switchMap(() => this.dashboardSvc.deleteCard(card.id))
      )
      .subscribe({
        next: () => {
          this.openSnackbar('Card excluído com sucesso');
        },
      });
  }

  protected onSelecionar() {
    const cards = this.cardsSel()
      ?.filter((card) => card.sel.value)
      .map((card) => ({
        id: card.id,
        linhasGrid: card.linhasGrid || 2,
        colunasGrid: card.colunasGrid || 2,
        prefUsuario: card.prefUsuario,
        origem: {
          venda: true,
          historico: true,
        },
      }));
    this.dashboardSvc.savePreferenciaUsuario({ cards: cards || [] });
    this.sidebarComponent()?.close();
  }

  protected onSortPredicate(
    index: number,
    _: CdkDrag<CardSelModel>,
    drop: CdkDropList<CardSelModel[]>
  ) {
    const card = drop.data[index];
    return card.sel.value;
  }

  // Métodos privados
  private configCards(): CardSelModel[] {
    const cards = this.dashboardSvc.cards();
    if (!cards) {
      return [];
    }

    const prefUsuario = this.prefUsuario();
    const cardsJaUtilizados = new Set<string>();
    const result: CardSelModel[] = [];

    // Organiza os cards primeiro na ordem que eles já estão selecionados
    prefUsuario?.cards.forEach((puCard) => {
      const card = cards.find((c) => c.id === puCard.id);
      if (card) {
        cardsJaUtilizados.add(card.id);
        result.push({
          ...card,
          ...puCard,
          sel: new FormControl(true, { nonNullable: true }),
        });
      }
    });

    // Depois adiciona os demais cards em ordem alfabética
    const novosCards = cards
      .filter((c) => !cardsJaUtilizados.has(c.id))
      .sort((ca, cb) => ca.titulo.localeCompare(cb.titulo))
      .map((c) => ({
        ...c,
        sel: new FormControl(false, { nonNullable: true }),
      }));
    return result.concat(novosCards);
  }

  private filterCards() {
    const cards = this.cardsSel();
    const filtro = this.searchSignal();
    if (!filtro || !cards) {
      return cards;
    }

    const filtoNormal = filtro ? NormalizeString(filtro) : '';
    const filteredCards = cards.filter((card) => {
      const tituloNormal = NormalizeString(card.titulo);
      return tituloNormal.indexOf(filtoNormal) !== -1;
    });
    return filteredCards;
  }
}
